import React, { useState } from 'react';
import { useProject } from '../context/ProjectContext';

const filterFolderBySearch = (folder, searchTerm) => {
  const filteredChildren = (folder.children || [])
    .map((child) => filterFolderBySearch(child, searchTerm))
    .filter(Boolean);

  const filteredProjects = (folder.projects || [])
    .filter((project) => project.name.toLowerCase().includes(searchTerm));

  const folderNameMatches = folder.name.toLowerCase().includes(searchTerm);

  if (!folderNameMatches && filteredChildren.length === 0 && filteredProjects.length === 0) {
    return null;
  }

  return {
    ...folder,
    children: filteredChildren,
    projects: filteredProjects,
  };
};

const FolderTree = ({ 
  folders, 
  projects, 
  currentProjectId,
  currentFolderId,
  onProjectSelect,
  onFolderSelect,
  onFolderCreate,
  onFolderRename,
  onFolderDelete,
  onFolderMove,
  onProjectMove,
  onProjectCreate,
  searchTerm = '',
  isPersonal = false 
}) => {
  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const hasActiveSearch = normalizedSearchTerm.length > 0;

  const visibleFolders = hasActiveSearch
    ? folders
      .map((folder) => filterFolderBySearch(folder, normalizedSearchTerm))
      .filter(Boolean)
    : folders;

  const visibleProjects = hasActiveSearch
    ? (projects || []).filter((project) => project.name.toLowerCase().includes(normalizedSearchTerm))
    : projects;

  return (
    <div className="space-y-1">
      {visibleFolders.map(folder => (
        <FolderNode
          key={folder.id}
          folder={folder}
          currentProjectId={currentProjectId}
          currentFolderId={currentFolderId}
          onProjectSelect={onProjectSelect}
          onFolderSelect={onFolderSelect}
          onFolderCreate={onFolderCreate}
          onFolderRename={onFolderRename}
          onFolderDelete={onFolderDelete}
          onFolderMove={onFolderMove}
          onProjectMove={onProjectMove}
          onProjectCreate={onProjectCreate}
          forceExpanded={hasActiveSearch}
          level={0}
        />
      ))}
      {visibleProjects && visibleProjects.length > 0 && (
        <div className="space-y-1">
          {visibleProjects.map(project => (
            <ProjectNode
              key={project.id}
              project={project}
              isActive={project.id === currentProjectId}
              onSelect={onProjectSelect}
              level={0}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const FolderNode = ({ 
  folder, 
  currentProjectId,
  currentFolderId,
  onProjectSelect,
  onFolderSelect,
  onFolderCreate,
  onFolderRename,
  onFolderDelete,
  onFolderMove,
  onProjectMove,
  onProjectCreate,
  forceExpanded = false,
  level = 0 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPos, setContextMenuPos] = useState({ x: 0, y: 0 });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showCreateSubfolderModal, setShowCreateSubfolderModal] = useState(false);
  const [newName, setNewName] = useState('');
  const [error, setError] = useState('');

  const handleContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenuPos({ x: e.clientX, y: e.clientY });
    setShowContextMenu(true);
  };

  const closeContextMenu = () => {
    setShowContextMenu(false);
  };

  const handleCreateSubfolder = () => {
    setNewName('');
    setError('');
    setShowCreateSubfolderModal(true);
    closeContextMenu();
  };

  const handleRename = () => {
    setNewName(folder.name);
    setError('');
    setShowRenameModal(true);
    closeContextMenu();
  };

  const handleDelete = () => {
    closeContextMenu();
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await onFolderDelete(folder.id);
      setShowDeleteModal(false);
    } catch (err) {
      setError(err.message || 'Failed to delete folder');
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setError('');
  };

  const handleConfirmRename = async () => {
    if (!newName || !newName.trim()) {
      setError('Folder name is required');
      return;
    }
    
    if (newName.trim() === folder.name) {
      setShowRenameModal(false);
      setNewName('');
      setError('');
      return;
    }

    try {
      await onFolderRename(folder.id, newName.trim());
      setShowRenameModal(false);
      setNewName('');
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to rename folder');
    }
  };

  const handleConfirmCreateSubfolder = async () => {
    if (!newName || !newName.trim()) {
      setError('Folder name is required');
      return;
    }

    try {
      await onFolderCreate(newName.trim(), folder.id);
      setShowCreateSubfolderModal(false);
      setNewName('');
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to create subfolder');
    }
  };

  const handleCreateProject = () => {
    if (onProjectCreate) {
      onProjectCreate(folder.id);
    }
    closeContextMenu();
  };

  const paddingLeft = `${level * 16 + 8}px`;
  const hasChildren = (folder.children && folder.children.length > 0) || (folder.projects && folder.projects.length > 0);
  const isSelected = currentFolderId === folder.id;

  React.useEffect(() => {
    if (forceExpanded) {
      setIsExpanded(true);
    }
  }, [forceExpanded]);

  const handleFolderClick = (e) => {
    // Don't trigger folder selection when clicking on expand/collapse button
    if (e.target.closest('button')) return;
    
    // Call onFolderSelect if provided
    if (onFolderSelect) {
      onFolderSelect(folder);
    }
  };

  return (
    <>
      <div>
        <div
          className={`flex items-center px-3 py-2 cursor-pointer group relative transition-colors ${
            isSelected 
              ? 'border-l-2'
              : ''
          }`}
          style={{
            ...(isSelected 
              ? { backgroundColor: 'rgba(130,255,207,0.08)', borderLeftColor: 'var(--tqa-green)' }
              : { borderLeft: '2px solid transparent' }),
            paddingLeft
          }}
          onContextMenu={handleContextMenu}
          onClick={handleFolderClick}
        >
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="mr-2 focus:outline-none flex-shrink-0" style={{ color: 'rgba(255,255,255,0.4)' }}
          >
            {hasChildren && (
              <svg
                className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            )}
            {!hasChildren && <div className="w-4 h-4" />}
          </button>
          
          <svg className="w-4 h-4 mr-2 flex-shrink-0" style={{ color: 'var(--tqa-green)' }} fill="currentColor" viewBox="0 0 20 20">
            <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
          </svg>
          
          <span className="text-sm font-medium truncate min-w-0 flex-1" style={{ color: isSelected ? 'var(--tqa-green)' : 'rgba(255,255,255,0.75)' }}>{folder.name}</span>
        </div>

        {isExpanded && (
          <div>
            {/* Child folders */}
            {folder.children && folder.children.length > 0 && (
              <div>
                {folder.children.map(childFolder => (
                  <FolderNode
                    key={childFolder.id}
                    folder={childFolder}
                    currentProjectId={currentProjectId}
                    currentFolderId={currentFolderId}
                    onProjectSelect={onProjectSelect}
                    onFolderSelect={onFolderSelect}
                    onFolderCreate={onFolderCreate}
                    onFolderRename={onFolderRename}
                    onFolderDelete={onFolderDelete}
                    onFolderMove={onFolderMove}
                    onProjectMove={onProjectMove}
                    onProjectCreate={onProjectCreate}
                    forceExpanded={forceExpanded}
                    level={level + 1}
                  />
                ))}
              </div>
            )}
            
            {/* Projects in this folder */}
            {folder.projects && folder.projects.length > 0 && (
              <div>
                {folder.projects.map(project => (
                  <ProjectNode
                    key={project.id}
                    project={project}
                    isActive={project.id === currentProjectId}
                    onSelect={onProjectSelect}
                    level={level + 1}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Context Menu */}
      {showContextMenu && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={closeContextMenu}
          />
          <div
            className="fixed z-50 py-1 min-w-[160px]"
            style={{ left: `${contextMenuPos.x}px`, top: `${contextMenuPos.y}px`, backgroundColor: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)' }}
          >
            <button
              onClick={handleCreateSubfolder}
              className="w-full text-left px-4 py-2 text-xs uppercase tracking-wider transition-colors hover:brightness-125"
              style={{ color: 'rgba(255,255,255,0.7)' }}
            >
              New Subfolder
            </button>
            <button
              onClick={handleCreateProject}
              className="w-full text-left px-4 py-2 text-xs uppercase tracking-wider transition-colors hover:brightness-125"
              style={{ color: 'rgba(255,255,255,0.7)' }}
            >
              New Project
            </button>
            <div className="my-1" style={{ borderTop: '1px solid var(--tqa-border-dark)' }} />
            <button
              onClick={handleRename}
              className="w-full text-left px-4 py-2 text-xs uppercase tracking-wider transition-colors hover:brightness-125"
              style={{ color: 'rgba(255,255,255,0.7)' }}
            >
              Rename
            </button>
            <button
              onClick={handleDelete}
              className="w-full text-left px-4 py-2 text-xs uppercase tracking-wider transition-colors hover:brightness-110"
              style={{ color: 'var(--tqa-red)' }}
            >
              Delete
            </button>
          </div>
        </>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0,0,0,0.75)' }}>
          <div className="p-6 w-96" style={{ backgroundColor: 'var(--tqa-surface)', border: '1px solid var(--tqa-border-dark)' }}>
            <h3 className="text-sm font-bold text-white uppercase tracking-widest mb-4">Delete Folder</h3>
            {folder.isPersonal && !folder.parentId && folder.name === 'Personal Workspace' ? (
              <>
                <p className="text-sm mb-6" style={{ color: 'var(--tqa-red)' }}>
                  The Personal Workspace folder cannot be deleted.
                </p>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleCancelDelete}
                    className="px-4 py-2 text-xs font-medium uppercase tracking-wider transition-colors"
                    style={{ border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.5)', backgroundColor: 'transparent' }}
                  >
                    Close
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm mb-6" style={{ color: 'rgba(255,255,255,0.7)' }}>
                  Are you sure you want to delete the folder <strong>"{folder.name}"</strong> and all its contents?
                  <br /><br />
                  This action cannot be undone.
                </p>
                {error && <p className="text-xs mb-4" style={{ color: 'var(--tqa-red)' }}>{error}</p>}
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={handleCancelDelete}
                    className="px-4 py-2 text-xs font-medium uppercase tracking-wider transition-colors"
                    style={{ border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.5)', backgroundColor: 'transparent' }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmDelete}
                    className="px-4 py-2 text-xs font-medium uppercase tracking-wider transition-colors hover:brightness-110"
                    style={{ backgroundColor: 'var(--tqa-red)', color: 'white' }}
                  >
                    Delete
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Rename Folder Modal */}
      {showRenameModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0,0,0,0.75)' }}>
          <div className="p-6 w-96" style={{ backgroundColor: 'var(--tqa-surface)', border: '1px solid var(--tqa-border-dark)' }}>
            <h3 className="text-sm font-bold text-white uppercase tracking-widest mb-4">Rename Folder</h3>
            <div className="mb-4">
              <label htmlFor="rename-folder-input" className="block text-xs font-medium uppercase tracking-wider mb-2" style={{ color: 'rgba(255,255,255,0.5)' }}>
                Folder Name
              </label>
              <input
                type="text"
                id="rename-folder-input"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleConfirmRename();
                  }
                }}
                className="w-full px-3 py-2 text-sm text-white focus:outline-none"
                style={{ backgroundColor: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)' }}
                placeholder="Enter folder name"
                autoFocus
              />
              {error && <p className="text-xs mt-1" style={{ color: 'var(--tqa-red)' }}>{error}</p>}
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowRenameModal(false);
                  setNewName('');
                  setError('');
                }}
                className="px-4 py-2 text-xs font-medium uppercase tracking-wider transition-colors"
                style={{ border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.5)', backgroundColor: 'transparent' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmRename}
                className="px-4 py-2 text-xs font-medium uppercase tracking-wider transition-colors hover:brightness-110 disabled:opacity-40"
                style={{ backgroundColor: 'var(--tqa-green)', color: 'var(--tqa-black)' }}
                disabled={!newName || !newName.trim() || newName.trim() === folder.name}
              >
                Rename
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Subfolder Modal */}
      {showCreateSubfolderModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0,0,0,0.75)' }}>
          <div className="p-6 w-96" style={{ backgroundColor: 'var(--tqa-surface)', border: '1px solid var(--tqa-border-dark)' }}>
            <h3 className="text-sm font-bold text-white uppercase tracking-widest mb-4">Create Subfolder</h3>
            <p className="text-xs mb-4" style={{ color: 'rgba(255,255,255,0.4)' }}>
              Creating subfolder in <strong style={{ color: 'rgba(255,255,255,0.7)' }}>"{folder.name}"</strong>
            </p>
            <div className="mb-4">
              <label htmlFor="create-subfolder-input" className="block text-xs font-medium uppercase tracking-wider mb-2" style={{ color: 'rgba(255,255,255,0.5)' }}>
                Folder Name
              </label>
              <input
                type="text"
                id="create-subfolder-input"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleConfirmCreateSubfolder();
                  }
                }}
                className="w-full px-3 py-2 text-sm text-white focus:outline-none"
                style={{ backgroundColor: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)' }}
                placeholder="Enter folder name"
                autoFocus
              />
              {error && <p className="text-xs mt-1" style={{ color: 'var(--tqa-red)' }}>{error}</p>}
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowCreateSubfolderModal(false);
                  setNewName('');
                  setError('');
                }}
                className="px-4 py-2 text-xs font-medium uppercase tracking-wider transition-colors"
                style={{ border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.5)', backgroundColor: 'transparent' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmCreateSubfolder}
                className="px-4 py-2 text-xs font-medium uppercase tracking-wider transition-colors hover:brightness-110 disabled:opacity-40"
                style={{ backgroundColor: 'var(--tqa-green)', color: 'var(--tqa-black)' }}
                disabled={!newName || !newName.trim()}
              >
                Create Folder
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const ProjectNode = ({ project, isActive, onSelect, level = 0 }) => {
  const { deleteProject, folders, moveProject } = useProject();
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPos, setContextMenuPos] = useState({ x: 0, y: 0 });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [newProjectName, setNewProjectName] = useState(project.name);
  const [selectedFolderId, setSelectedFolderId] = useState(project.folderId || null);
  const paddingLeft = `${level * 16 + 32}px`; // Extra indent for projects under folders

  const handleContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenuPos({ x: e.clientX, y: e.clientY });
    setShowContextMenu(true);
  };

  const closeContextMenu = () => {
    setShowContextMenu(false);
  };

  const handleMoveClick = () => {
    closeContextMenu();
    setShowMoveModal(true);
  };

  const handleRenameClick = () => {
    closeContextMenu();
    setNewProjectName(project.name);
    setShowRenameModal(true);
  };

  const handleDeleteClick = () => {
    closeContextMenu();
    setShowDeleteModal(true);
  };

  const handleConfirmMove = async () => {
    try {
      await moveProject(project.id, selectedFolderId);
      setShowMoveModal(false);
      setSelectedFolderId(project.folderId || null);
    } catch (err) {
      console.error('Error moving project:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to move project';
      alert(errorMessage);
    }
  };

  const handleCancelMove = () => {
    setShowMoveModal(false);
    setSelectedFolderId(project.folderId || null);
  };

  const handleConfirmRename = async () => {
    try {
      if (!newProjectName || !newProjectName.trim() || newProjectName.trim() === project.name) {
        setShowRenameModal(false);
        return;
      }

      const { projectService } = await import('../services/api');
      await projectService.updateProject(project.id, { name: newProjectName.trim() });
      setShowRenameModal(false);
      // Reload to reflect changes
      window.location.reload();
    } catch (err) {
      alert(err.message || 'Failed to rename project');
    }
  };

  const handleCancelRename = () => {
    setShowRenameModal(false);
    setNewProjectName(project.name);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteProject(project.id);
      setShowDeleteModal(false);
    } catch (err) {
      alert(err.message || 'Failed to delete project');
      setShowDeleteModal(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
  };

  // Helper function to recursively render folder options in select dropdown
  const renderFolderOptions = (folderList, depth = 0) => {
    const options = [];
    const indent = '\u00A0\u00A0'.repeat(depth); // Non-breaking spaces for indentation

    folderList.forEach(folder => {
      options.push(
        <option key={folder.id} value={folder.id}>
          {indent}{depth > 0 ? '└ ' : ''}{folder.name}
        </option>
      );

      // Recursively add child folders
      if (folder.children && folder.children.length > 0) {
        options.push(...renderFolderOptions(folder.children, depth + 1));
      }
    });

    return options;
  };

  return (
    <>
      <button
        onClick={() => onSelect(project.id)}
        onContextMenu={handleContextMenu}
        className={`w-full text-left flex items-center px-3 py-2 text-sm transition-colors border-l-2`}
        style={{
          ...(isActive
            ? { backgroundColor: 'rgba(130,255,207,0.08)', color: 'var(--tqa-green)', borderLeftColor: 'var(--tqa-green)' }
            : { color: 'rgba(255,255,255,0.7)', borderLeftColor: 'transparent' }),
          paddingLeft
        }}
      >
        <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <span className="truncate">{project.name}</span>
      </button>

      {/* Context Menu */}
      {showContextMenu && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={closeContextMenu}
          />
          <div
            className="fixed z-50 py-1 min-w-[160px]"
            style={{ left: `${contextMenuPos.x}px`, top: `${contextMenuPos.y}px`, backgroundColor: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)' }}
          >
            <button
              onClick={handleRenameClick}
              className="w-full text-left px-4 py-2 text-xs uppercase tracking-wider transition-colors hover:brightness-125"
              style={{ color: 'rgba(255,255,255,0.7)' }}
            >
              Rename
            </button>
            <button
              onClick={handleMoveClick}
              className="w-full text-left px-4 py-2 text-xs uppercase tracking-wider transition-colors hover:brightness-125"
              style={{ color: 'rgba(255,255,255,0.7)' }}
            >
              Move to Folder
            </button>
            <div className="my-1" style={{ borderTop: '1px solid var(--tqa-border-dark)' }} />
            <button
              onClick={handleDeleteClick}
              className="w-full text-left px-4 py-2 text-xs uppercase tracking-wider transition-colors hover:brightness-110"
              style={{ color: 'var(--tqa-red)' }}
            >
              Delete
            </button>
          </div>
        </>
      )}

      {/* Rename Project Modal */}
      {showRenameModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0,0,0,0.75)' }}>
          <div className="p-6 w-96" style={{ backgroundColor: 'var(--tqa-surface)', border: '1px solid var(--tqa-border-dark)' }}>
            <h3 className="text-sm font-bold text-white uppercase tracking-widest mb-4">Rename Project</h3>
            <div className="mb-4">
              <label htmlFor="rename-project-input" className="block text-xs font-medium uppercase tracking-wider mb-2" style={{ color: 'rgba(255,255,255,0.5)' }}>
                Project Name
              </label>
              <input
                type="text"
                id="rename-project-input"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleConfirmRename();
                  }
                }}
                className="w-full px-3 py-2 text-sm text-white focus:outline-none"
                style={{ backgroundColor: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)' }}
                placeholder="Enter project name"
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={handleCancelRename}
                className="px-4 py-2 text-xs font-medium uppercase tracking-wider transition-colors"
                style={{ border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.5)', backgroundColor: 'transparent' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmRename}
                className="px-4 py-2 text-xs font-medium uppercase tracking-wider transition-colors hover:brightness-110 disabled:opacity-40"
                style={{ backgroundColor: 'var(--tqa-green)', color: 'var(--tqa-black)' }}
                disabled={!newProjectName || !newProjectName.trim() || newProjectName.trim() === project.name}
              >
                Rename
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Move Project Modal */}
      {showMoveModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0,0,0,0.75)' }}>
          <div className="p-6 w-96" style={{ backgroundColor: 'var(--tqa-surface)', border: '1px solid var(--tqa-border-dark)' }}>
            <h3 className="text-sm font-bold text-white uppercase tracking-widest mb-4">Move Project</h3>
            <p className="text-sm mb-4" style={{ color: 'rgba(255,255,255,0.6)' }}>
              Move <strong style={{ color: 'white' }}>"{project.name}"</strong> to a different folder:
            </p>
            <div className="mb-6">
              <label htmlFor="move-folder" className="block text-xs font-medium uppercase tracking-wider mb-2" style={{ color: 'rgba(255,255,255,0.5)' }}>
                Select Folder
              </label>
              <select
                id="move-folder"
                value={selectedFolderId || ''}
                onChange={(e) => setSelectedFolderId(e.target.value || null)}
                className="w-full px-3 py-2 text-sm text-white focus:outline-none"
                style={{ backgroundColor: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)' }}
              >
                <option value="">Select a folder</option>
                {folders.shared && folders.shared.length > 0 && (
                  <optgroup label="Shared Folders">
                    {renderFolderOptions(folders.shared, 0)}
                  </optgroup>
                )}
                {folders.personal && folders.personal.length > 0 && (
                  <optgroup label="Personal Workspace">
                    {renderFolderOptions(folders.personal, 0)}
                  </optgroup>
                )}
              </select>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={handleCancelMove}
                className="px-4 py-2 text-xs font-medium uppercase tracking-wider transition-colors"
                style={{ border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.5)', backgroundColor: 'transparent' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmMove}
                className="px-4 py-2 text-xs font-medium uppercase tracking-wider transition-colors hover:brightness-110 disabled:opacity-40"
                style={{ backgroundColor: 'var(--tqa-green)', color: 'var(--tqa-black)' }}
                disabled={!selectedFolderId}
              >
                Move Project
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Project Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0,0,0,0.75)' }}>
          <div className="p-6 w-96" style={{ backgroundColor: 'var(--tqa-surface)', border: '1px solid var(--tqa-border-dark)' }}>
            <h3 className="text-sm font-bold text-white uppercase tracking-widest mb-4">Delete Project</h3>
            <p className="text-sm mb-6" style={{ color: 'rgba(255,255,255,0.7)' }}>
              Are you sure you want to delete the project <strong>"{project.name}"</strong>?
              <br /><br />
              This action cannot be undone and will remove all associated data.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={handleCancelDelete}
                className="px-4 py-2 text-xs font-medium uppercase tracking-wider transition-colors"
                style={{ border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.5)', backgroundColor: 'transparent' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                className="px-4 py-2 text-xs font-medium uppercase tracking-wider transition-colors hover:brightness-110"
                style={{ backgroundColor: 'var(--tqa-red)', color: 'white' }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FolderTree;
